// The Betfair Hub's own ratings, joined onto the card the device is holding.
//
// WHAT THEY ARE. Two free models the Hub publishes daily and stands behind
// neither of: **Kash** rates Australian thoroughbred races and **Iggy** rates
// greyhounds, each as a rated price per runner plus an early-speed figure.
// Nobody publishes a harness model, so the trots carry none and the screens say
// nothing rather than printing a blank.
//
// A SECOND OPINION, NEVER A TIP, and everything here is built to keep that
// true. A rating changes no price, no fair price, no consensus and no stake: it
// rides on `match.runners[rN].rating` and the only number derived from it is
// `ratingDisagreement` (engine/rules.js), which a strategy has to name in a
// rule before it means anything at all. As shipped no strategy does.
//
// THE JOIN IS THE VENUE MATCHER, NOT A SECOND ONE. `venueId` in
// match-exchange.js is the one place in this app that turns a track's name into
// an id — sponsor off the front, code off the back, then the alias table — and
// the Hub spells tracks its own way ("Royal Randwick", "Sandown Hillside") just
// as Betfair does. A second slugger here would be a second answer to the
// question that decides whether Randwick's ratings land on Rosehill's runners.
//
// THE RUNNER JOINS ON ITS SADDLECLOTH and on nothing else. A name match is what
// `match-exchange.js` needs because two feeds spell a horse differently; the
// Hub publishes the cloth number for the gallops (in the runner's name) and the
// box number for the dogs (in its own column), and `server/history/ratingsCsv.mjs`
// has already read both into a plain number by the time anything here sees it.
// A race where the numbering disagrees is a race with no ratings on it, which
// is the honest answer.
//
// PURE. Features and rows go in, rows come out; nothing fetches, and the rows
// that go in are never mutated.

import { venueId } from './match-exchange.js';

/** A number, or null — `Number(null)` is 0 and a box 0 does not exist. */
const finite = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
};

/** `${venueId}|${raceNumber}` — what a race is looked up by. */
const raceId = (venue, number) => `${venueId(venue)}|${number}`;

/**
 * The day's features as a lookup: `Map<'randwick|5', Map<cloth, rating>>`.
 *
 * In goes what `features/racing/{date}.json` holds — `{ sports: { racing_horse:
 * { model, races: [{ venue, race, runners: [{ cloth, name, ratedPrice,
 * earlySpeed }] }] } } }` — and out comes the one shape a card row can ask a
 * question of.
 *
 * The SPORT is part of neither key, deliberately: a venue and a race number
 * already identify a meeting's race, the two models never rate the same code,
 * and a greyhound track that shares a name with a gallops track (Sandown,
 * Bendigo, Healesville) is exactly the case `venueId`'s alias table separates.
 */
export function ratingIndex(features) {
  const byRace = new Map();
  for (const sport of Object.values(features?.sports ?? {})) {
    for (const race of sport?.races ?? []) {
      const number = finite(race?.race);
      const venue = venueId(race?.venue);
      if (!venue || number === null) continue;
      const runners = byRace.get(raceId(race.venue, number)) ?? new Map();
      for (const runner of race?.runners ?? []) {
        const cloth = finite(runner?.cloth);
        const ratedPrice = finite(runner?.ratedPrice);
        if (cloth === null || ratedPrice === null || ratedPrice <= 1) continue;
        // First rating per saddlecloth wins, the same rule every other join in
        // this folder follows: a second one is a race published twice.
        if (!runners.has(cloth)) {
          runners.set(cloth, { ratedPrice, earlySpeed: finite(runner?.earlySpeed) });
        }
      }
      if (runners.size) byRace.set(raceId(race.venue, number), runners);
    }
  }
  return byRace;
}

/**
 * The card's rows with the Hub's rating on the runners it rated.
 *
 * `rows` is what `fromRaces` answered: one per race x runner x bookmaker, each
 * carrying the whole race bundle including `runners`. The bundle is REBUILT per
 * race rather than written into, because every row of a race shares one object
 * and mutating it would reach back into the stored card.
 *
 * Answers the rows unchanged when there is nothing to join, which is the
 * ordinary case: no features for the day, a harness card, or a meeting the Hub
 * does not rate.
 */
export function attachRatings(rows, features) {
  const index = ratingIndex(features);
  if (!index.size || !rows?.length) return rows ?? [];

  // One rebuilt `runners` per race, so every row of that race gets the same
  // object and a race with no ratings keeps the object it arrived with.
  const rebuilt = new Map();
  let touched = 0;

  const runnersFor = (row) => {
    if (rebuilt.has(row.matchKey)) return rebuilt.get(row.matchKey);
    const runners = index.get(raceId(row.venue ?? row.venueId, row.raceNumber));
    if (!runners || !row.runners) {
      rebuilt.set(row.matchKey, null);
      return null;
    }
    const next = {};
    let hit = 0;
    for (const [selection, runner] of Object.entries(row.runners)) {
      const rating = runners.get(finite(runner?.number));
      next[selection] = rating ? { ...runner, rating: { ...rating } } : runner;
      if (rating) hit += 1;
    }
    const answer = hit ? next : null;
    if (hit) touched += 1;
    rebuilt.set(row.matchKey, answer);
    return answer;
  };

  const out = rows.map((row) => {
    const runners = runnersFor(row);
    return runners ? { ...row, runners } : row;
  });
  return touched ? out : rows;
}

/** "Hub rated 3.40" — the whole of what a runner's rating says on a screen. */
export function ratingLine(rating) {
  const price = Number(rating?.ratedPrice);
  return Number.isFinite(price) && price > 1 ? `Hub rated ${price.toFixed(2)}` : '';
}

/**
 * The one sentence a race page prints under a field that carries ratings, once.
 *
 * It says what they are and who made them, and it says what they are not. The
 * app has never tested whether following them wins — the R7 harness has run the
 * question and the answer is in HANDOFF — so "not a tip" is a statement of what
 * is known rather than a disclaimer.
 */
export const RATINGS_NOTE = 'Rated prices are the Betfair Hub’s own free models, not a tip and not Legwork’s opinion. Nothing on this page is priced off them.';
